import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { postsAPI } from '../../services/api';
import { Card, Button, StatusBadge } from '../ui';
import Loading from '../common/Loading';
import { formatDate, handleApiError, getImageUrl } from '../../utils/helpers';
import { PLATFORMS, CONTENT_TYPES } from '../../utils/platformConfig';
import { PlatformIcon } from '../icons/PlatformIcons';
import { ArrowLeft, Image as ImageIcon, Calendar, User, LayoutTemplate } from 'lucide-react';

const PostReviewSimpleA = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isClient } = useAuth();
  const { isRTL } = useLanguage();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await postsAPI.getById(id);
        const postData = response.data.post;

        if (postData.current_image_url) {
          postData.current_image_url = getImageUrl(postData.current_image_url);
        }

        setPost(postData);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error || !post) {
    return (
      <div className="max-w-4xl mx-auto py-16 text-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <p className="text-red-600 mb-4">{error || (isRTL ? 'لم يتم العثور على البوست' : 'Post not found')}</p>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          {isRTL ? 'رجوع' : 'Back'}
        </Button>
      </div>
    );
  }

  const platformInfo = post.platform ? PLATFORMS[post.platform] : null;
  const contentTypeInfo = post.content_type ? CONTENT_TYPES[post.content_type] : null;

  return (
    <div className="max-w-6xl mx-auto space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="p-5 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div className="flex items-start justify-between gap-4">
          <div className={`flex items-start gap-3 flex-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-white/80 transition-colors mt-1"
            >
              <ArrowLeft className={`w-5 h-5 text-slate-600 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <div className="inline-flex items-center gap-2 mb-1">
                <LayoutTemplate className="w-4 h-4 text-sky-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-sky-600">
                  {isRTL ? 'تصميم تجريبي A' : 'Experimental Layout A'}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                {post.title || (isRTL ? 'بوست بدون عنوان' : 'Untitled Post')}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                {post.client_name && (
                  <span className="inline-flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {post.client_name}
                  </span>
                )}
                {post.created_at && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(post.created_at)}
                  </span>
                )}
                {platformInfo && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/80 border border-slate-200">
                    <PlatformIcon platformId={platformInfo.id} className="w-3 h-3" />
                    <span className="text-[11px]">
                      {platformInfo.icon}{' '}
                      {isRTL ? platformInfo.nameAr : platformInfo.name}
                    </span>
                  </span>
                )}
                {contentTypeInfo && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-900 text-white text-[11px]">
                    {contentTypeInfo.icon}
                    {isRTL ? contentTypeInfo.nameAr : contentTypeInfo.name}
                  </span>
                )}
                <StatusBadge status={post.status} />
              </div>
            </div>
          </div>

          {/* Link to full page */}
          <div className="flex items-start">
            <Link to={`/posts/${post.id}`}>
              <Button variant="secondary" size="sm">
                {isRTL ? 'الصفحة الكاملة' : 'Full Page'}
              </Button>
            </Link>
          </div>
        </div>
      </Card>

      {/* Two-column layout: big preview + simple info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Preview column */}
        <Card className="relative overflow-hidden rounded-2xl border-slate-200 bg-slate-950">
          <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top,_#0ea5e9_0,_transparent_55%),_radial-gradient(circle_at_bottom,_#22c55e_0,_transparent_55%)] opacity-40" />
          <div className="relative p-4 md:p-6 flex flex-col h-full">
            <div className="flex items-center justify-between mb-4 text-white">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {isRTL ? 'معاينة البوست' : 'Post Preview'}
                </span>
              </div>
              {platformInfo && (
                <div className="flex items-center gap-1 text-xs">
                  <PlatformIcon platformId={platformInfo.id} className="w-4 h-4" />
                  <span>{platformInfo.icon}</span>
                </div>
              )}
            </div>

            <div className="flex-1 flex items-center justify-center rounded-xl bg-slate-900/80 border border-slate-800 overflow-hidden">
              {post.current_image_url ? (
                <img
                  src={post.current_image_url}
                  alt={post.title || 'Post'}
                  className="max-h-[520px] w-auto object-contain"
                />
              ) : (
                <div className="flex flex-col items-center justify-center text-slate-400 py-16">
                  <ImageIcon className="w-10 h-10 mb-3" />
                  <p className="text-sm">
                    {isRTL ? 'لا توجد صورة حالية لهذا البوست' : 'No current image for this post'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Info / caption column */}
        <div className="space-y-4">
          <Card className="p-5 rounded-2xl border-slate-200 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">
              {isRTL ? 'الكابتشن الحالي' : 'Current Caption'}
            </h2>
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 max-h-64 overflow-y-auto">
              <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                {post.current_caption ||
                  (isRTL ? 'لا يوجد كابتشن حالي' : 'No current caption')}
              </p>
            </div>
          </Card>

          <Card className="p-5 rounded-2xl border-slate-200 shadow-sm space-y-3">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
              {isRTL ? 'معلومات مختصرة' : 'Quick Info'}
            </h2>
            <div className="space-y-2 text-sm text-slate-700">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">
                  {isRTL ? 'الحالة' : 'Status'}
                </span>
                <StatusBadge status={post.status} />
              </div>
              {post.serial_number && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">
                    {isRTL ? 'رقم البوست' : 'Post #'}
                  </span>
                  <span className="font-medium text-slate-900">
                    {post.serial_number}
                  </span>
                </div>
              )}
              {post.platform && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">
                    {isRTL ? 'المنصة' : 'Platform'}
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm">
                    {platformInfo && (
                      <>
                        <PlatformIcon platformId={platformInfo.id} className="w-4 h-4" />
                        <span>
                          {isRTL ? platformInfo.nameAr : platformInfo.name}
                        </span>
                      </>
                    )}
                  </span>
                </div>
              )}
              {contentTypeInfo && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">
                    {isRTL ? 'نوع المحتوى' : 'Content Type'}
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm">
                    <span>{contentTypeInfo.icon}</span>
                    <span>
                      {isRTL ? contentTypeInfo.nameAr : contentTypeInfo.name}
                    </span>
                  </span>
                </div>
              )}
            </div>
          </Card>

          {!isClient && (
            <Card className="p-4 rounded-2xl border-dashed border-slate-300 text-xs text-slate-500">
              {isRTL
                ? 'هذه صفحة تجريبية للمعاينة فقط، بدون كل التفاصيل والإجراءات الموجودة في الصفحة الأساسية.'
                : 'This is an experimental preview-only layout, without all the actions and details from the main page.'}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostReviewSimpleA;

